import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { X, Smartphone } from "lucide-react";

/**
 * Shows a friendly "Install PaliStore on your phone" banner.
 * - On Android Chrome: listens to `beforeinstallprompt` and triggers native install.
 * - On iOS Safari: shows instructions (no native install on iOS).
 * - Stores dismissal in localStorage so it doesn't nag.
 */
const STORAGE_KEY = "palistore-install-banner-dismissed";

export default function InstallPwaBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return;

    // Check if already running as PWA
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    const ua = window.navigator.userAgent.toLowerCase();
    const ios = /iphone|ipad|ipod/.test(ua);
    setIsIOS(ios);

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS doesn't fire beforeinstallprompt — show banner after 4s
    if (ios) {
      const t = setTimeout(() => setShow(true), 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", handler);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setShow(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShow(false);
    localStorage.setItem(STORAGE_KEY, "1");
  };

  if (!show) return null;

  return (
    <div
      data-testid="install-pwa-banner"
      className="fixed bottom-20 left-3 right-3 md:left-auto md:right-4 md:bottom-20 md:max-w-sm z-[9000] rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-2xl border border-white/20 backdrop-blur-sm"
    >
      <div className="p-3 flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5 bg-white/20 rounded-lg p-2">
          <Smartphone className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">Instala PaliStore</p>
          {isIOS ? (
            <p className="text-xs text-white/90 mt-0.5 leading-tight">
              Toca el botón <span className="font-bold">Compartir</span> de Safari y luego{" "}
              <span className="font-bold">"Añadir a pantalla de inicio"</span>.
            </p>
          ) : (
            <p className="text-xs text-white/90 mt-0.5 leading-tight">
              Úsalo como app desde tu pantalla principal. Más rápido, sin barra del navegador.
            </p>
          )}
          {!isIOS && (
            <Button
              onClick={install}
              size="sm"
              variant="secondary"
              className="mt-2 bg-white text-purple-700 hover:bg-white/90 h-8"
              data-testid="install-pwa-button"
            >
              Instalar
            </Button>
          )}
        </div>
        <button
          onClick={dismiss}
          className="text-white/80 hover:text-white p-1 -mt-1 -mr-1"
          aria-label="Cerrar"
          data-testid="dismiss-install-banner"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
